import { ReactNode, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Select, SelectItem } from '@mantine/core';
import { useIntlLight } from '../../../i18n';
import { generateYearsInterval } from '../../utils/generateYearsInterval';

export default function YearPicker({
  className,
  name,
  startYearLabel,
  endYearLabel,
  startYearPlaceholder,
  endYearPlaceholder,
  required,
  error,
  value,
  onChange,
  icon,
  minYear = (dayjs().year() - 3).toString(),
}: {
  className?: string;
  name?: string;
  startYearLabel?: string;
  endYearLabel?: string;
  startYearPlaceholder?: string;
  endYearPlaceholder?: string;
  required?: boolean;
  error?: any;
  value?: string[];
  onChange?: ((value: string[] | null) => void) | undefined;
  icon?: ReactNode;
  minYear?: string;
}) {
  const { t } = useIntlLight();
  const [startYear, setStartYear] = useState<string | undefined>(value?.[0]);
  const [endYear, setEndYear] = useState<string | undefined>(value?.[1]);

  useEffect(() => {
    setStartYear(value?.[0]);
    setEndYear(value?.[1]);
  }, [value]);

  const startYearOptions = generateYearsInterval(minYear)?.map(year => {
    const selectableYear: SelectItem = {
      value: year,
      label: year,
      selected: year === startYear,
    };

    return selectableYear;
  });

  const endYearOptions = generateYearsInterval(minYear)?.map(year => {
    const selectableYear: SelectItem = {
      value: year,
      label: year,
      disabled: Boolean(startYear && year < startYear),
      selected: year === endYear,
    };

    return selectableYear;
  });

  return (
    <>
      <Select
        className={className}
        name={`${name}[0]`}
        label={startYearLabel ?? t('field.startYear')}
        placeholder={startYearPlaceholder ?? t('app.selectYear')}
        value={startYear}
        required={required}
        data={startYearOptions}
        error={error?.message}
        onChange={year => {
          if (year) {
            setStartYear(year);
            if (endYear && year > endYear) {
              setEndYear(year);
              onChange?.([year, year]);
            } else if (endYear) {
              onChange?.([year, endYear]);
            } else {
              onChange?.([year, '']);
            }
          } else {
            setStartYear(undefined);
            if (endYear) {
              onChange?.(['', endYear]);
            } else {
              onChange?.(null);
            }
          }
        }}
        icon={icon}
      />
      <Select
        className={className}
        name={`${name}[1]`}
        label={endYearLabel ?? t('field.endYear')}
        placeholder={endYearPlaceholder ?? t('app.selectYear')}
        value={endYear}
        required={required}
        data={endYearOptions}
        error={error?.message}
        onChange={year => {
          if (year) {
            setEndYear(year);
            if (startYear) {
              onChange?.([startYear, year]);
            } else {
              onChange?.(['', year]);
            }
          } else {
            setEndYear(undefined);
            if (startYear) {
              onChange?.([startYear, '']);
            } else {
              onChange?.(null);
            }
          }
        }}
        icon={icon}
      />
    </>
  );
}
